import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { jwtDecode } from "jwt-decode";
import { API_BASE_URL } from "../config";
import { fetchStudentById } from "./studentsSlice"; // Adjust path if needed

// Get initial state from localStorage if available
const storedUser = localStorage.getItem("userInfo");
const initialState = {
  isAuthenticated: !!storedUser,
  userInfo: storedUser ? JSON.parse(storedUser) : null,
  qrAuthInProgress: false,
  error: null,
  forgotPasswordStatus: "idle",
  resetPasswordStatus: "idle",
};
export const loginUser = createAsyncThunk(
  "auth/login",
  async ({ username, password }, { dispatch, rejectWithValue }) => {  
    try {
      const response = await fetch(`${API_BASE_URL}/api/Account/login`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ username, password }),
      });
      const data = await response.json();
      if (!response.ok) {
        return rejectWithValue(data.message || "Login failed");
      }

      const tokenParts = data.token.split(".");
      const tokenPayload = JSON.parse(atob(tokenParts[1]));

      let combined_roles = data?.roles || [];
      if (tokenPayload.role) {
        if (Array.isArray(tokenPayload.role)) {
          combined_roles = [
            ...new Set([...combined_roles, ...tokenPayload.role]),
          ];
        } else if (typeof tokenPayload.role === "string") {
          let payload_roles = tokenPayload.role.includes(",")
            ? tokenPayload.role.split(",").map((role) => role.trim())
            : [tokenPayload.role];
          combined_roles = [
            ...new Set([...combined_roles, ...payload_roles]),
          ];
        }
      } 

     // --- build normalized userData from response ---
const studentIdsFromResponse = Array.isArray(data.students)
  ? data.students.map((s) => s.studentId).filter(Boolean)
  : [];

// combine token payload roles and API roles as you already do...
const userData = {
  token: data.token,
  roles: combined_roles || data.roles,
  // normalized list of student ids (may be empty)
  studentIds: studentIdsFromResponse,
  staffId: data.staffId || "",
  parentId: data.parentId || "",
  allowedEndpoints: data.allowedEndpoints || [],
  username: tokenPayload.unique_name || username,
  email: tokenPayload.email,
  userId: tokenPayload.nameid || data.userId,
};

// --- fetch student details (await results so we can attach them) ---
const fullStudentInfo = [];

if (combined_roles.includes("Parent") && userData.studentIds.length > 0) {
  // fetch each child, sequentially so order is preserved
  for (const sid of userData.studentIds) {
    const actionResult = await dispatch(fetchStudentById(sid));
    if (fetchStudentById.fulfilled.match(actionResult)) {
      fullStudentInfo.push(actionResult.payload);
    }
  }
} else if (combined_roles.includes("Student")) {
  // prefer the first studentId if provided, otherwise fallback to userId
  const studentId =
    userData.studentIds.length > 0 ? userData.studentIds[0] : userData.userId;
  const actionResult = await dispatch(fetchStudentById(studentId));
  if (fetchStudentById.fulfilled.match(actionResult)) {
    fullStudentInfo.push(actionResult.payload);
  }
} else {
  // fallback: try fetching by userId
  const actionResult = await dispatch(fetchStudentById(userData.userId));
  if (fetchStudentById.fulfilled.match(actionResult)) {
    fullStudentInfo.push(actionResult.payload);
  }
}

// attach to userData
userData.fullStudentInfo = fullStudentInfo;

// now persist userData / token as you already do
localStorage.clear();
localStorage.setItem("userInfo", JSON.stringify(userData));
localStorage.setItem("token", data.token);
if (tokenPayload.exp) {
  const expirationTime = tokenPayload.exp * 1000;
  localStorage.setItem("tokenExpiration", expirationTime.toString());
}

      // New logic for fetching session data based on roles:
      if (combined_roles.includes("Parent") && Array.isArray(userData.studentIds)) {
        // Loop through studentIds and fetch data for each student
        userData.studentIds.forEach(studentId => {
          dispatch(fetchStudentById(studentId));
        });
      } else if (combined_roles.includes("Student")) {
        // For student role, fetch only their own studentId if available, else userId
        const studentId = userData.studentIds && userData.studentIds.length > 0
          ? userData.studentIds[0]
          : userData.userId;
        dispatch(fetchStudentById(studentId));
      } else {
        // Optional: handle other roles or fallback
        dispatch(fetchStudentById(userData.userId));
      }

      return userData;
    } catch (error) {
      return rejectWithValue(error.message || "An unexpected error occurred");
    }
  }
);

export const loginWithQR = createAsyncThunk(
  "auth/loginQR",
  async (userData, { dispatch, rejectWithValue }) => {
    try {
      localStorage.setItem("userInfo", JSON.stringify(userData));
      localStorage.setItem("token", userData.token);

      // Same logic here for QR login:
      if (userData.roles.includes("Parent") && Array.isArray(userData.studentIds)) {
        userData.studentIds.forEach(studentId => {
          dispatch(fetchStudentById(studentId));
        });
      } else if (userData.roles.includes("Student")) {
        const studentId = userData.studentIds && userData.studentIds.length > 0
          ? userData.studentIds[0]
          : userData.userId;
        dispatch(fetchStudentById(studentId));
      } else {
        dispatch(fetchStudentById(userData.userId));
      }

      return userData;
    } catch (error) {
      return rejectWithValue(error.message || "QR login failed");
    }
  }
);


export const forgotPassword = createAsyncThunk(
  "auth/forgotPassword",
  async (username, { rejectWithValue }) => {
    try {
      const response = await fetch(
        `${API_BASE_URL}/api/Account/request-password-reset`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ username }),
        }
      );

      if (!response.ok) {
        throw new Error("Failed to send reset password email");
      }

      return await response.json();
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const resetPassword = createAsyncThunk(
  "auth/resetPassword",
  async ({ userName, token, newPassword }, { rejectWithValue }) => {
    try {
      const response = await fetch(
        `${API_BASE_URL}/api/Account/reset-password`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ userName, token, newPassword }),
        }
      );

      if (!response.ok) {
        throw new Error("Failed to reset password");
      }

      return await response.json();
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

// Add this in your slice file where other thunks are defined
export const changePassword = createAsyncThunk(
  "auth/changePassword",
  async ({ currentPassword, newPassword }, { rejectWithValue }) => {
    try {
           console.log(
             "currentPassword, newPassword ",
             currentPassword,
             newPassword
           );

      const response = await fetch(
        `${API_BASE_URL}/api/Account/change-password`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
          body: JSON.stringify({ currentPassword, newPassword }),
        }
      );
      console.log("response", response);
      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.message || "Failed to change password");
      }

      return await response.json();
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);


export const logoutUser = () => {
  localStorage.removeItem("userInfo");
  window.location.href = "/login";
};

export const authSlice = createSlice({
  name: "auth",
  initialState,
  reducers: {
    setAuthenticated: (state, action) => {
      const {
        token,
        roles,
        userId,
        username,
        studentIds,
        parentId,
        staffId,
        email,
        allowedEndpoints,
      } = action.payload;
      state.isAuthenticated = true;
      state.userInfo = {
        userId,
        username,
        studentIds,
        parentId,
        staffId,
        email,
        roles,
        token,
        allowedEndpoints,
      };
    },
    setQRAuthInProgress: (state, action) => {
      state.qrAuthInProgress = action.payload;
    },
    logout: (state) => {
      state.isAuthenticated = false;
      state.userInfo = null;
      state.qrAuthInProgress = false;
      state.error = null;
      logoutUser();
    },
    clearError: (state) => {
      state.error = null;
    },
    clearAuthStatus: (state) => {
      state.forgotPasswordStatus = "idle";
      state.resetPasswordStatus = "idle";
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(loginUser.pending, (state) => {
        state.error = null;
      })
      .addCase(loginUser.fulfilled, (state, action) => {
        state.isAuthenticated = true;
        state.userInfo = action.payload; // Store the user info here
        state.error = null;
      })
      .addCase(loginUser.rejected, (state, action) => {
        state.isAuthenticated = false;
        state.userInfo = null; // Clear user info on failed login
        state.error = action.payload;
      })
      .addCase(loginWithQR.fulfilled, (state, action) => {
        state.isAuthenticated = true;
        state.userInfo = action.payload; // Store the user info from QR login
        state.error = null;
      })
      .addCase(loginWithQR.rejected, (state, action) => {
        state.isAuthenticated = false;
        state.userInfo = null; // Clear user info on failed QR login
        state.error = action.payload;
      })

      .addCase(forgotPassword.pending, (state) => {
        state.forgotPasswordStatus = "loading";
        state.error = null;
      })
      .addCase(forgotPassword.fulfilled, (state) => {
        state.forgotPasswordStatus = "succeeded";
      })
      .addCase(forgotPassword.rejected, (state, action) => {
        state.forgotPasswordStatus = "failed";
        state.error = action.payload;
      })
      .addCase(resetPassword.pending, (state) => {
        state.resetPasswordStatus = "loading";
        state.error = null;
      })
      .addCase(resetPassword.fulfilled, (state) => {
        state.resetPasswordStatus = "succeeded";
      })
      .addCase(resetPassword.rejected, (state, action) => {
        state.resetPasswordStatus = "failed";
        state.error = action.payload;
      });
    // .addCase(validateToken.rejected, (state, action) => {
    //   state.error = action.payload;
    // });
  },
});

export const {
  setAuthenticated,
  setQRAuthInProgress,
  logout,
  clearError,
  clearAuthStatus,
} = authSlice.actions;

export default authSlice.reducer;

